<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Draggable from 'vuedraggable';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import SelectMenu from 'dashboard/components-next/selectmenu/SelectMenu.vue';
import { UNASSIGNED_KANBAN_COLUMN_VALUE } from 'dashboard/constants/kanban';

const props = defineProps({
  // [{ attributeKey, attributeDisplayName, attributeValues }]
  listAttributes: { type: Array, default: () => [] },
  groupByAttributeKey: { type: String, default: '' },
  // ordered subset of the active attribute's values shown as columns
  columnOrder: { type: Array, default: () => [] },
});

const emit = defineEmits(['update:group-by', 'update:column-order']);

const { t } = useI18n();

const isOpen = ref(false);

const attributeOptions = computed(() =>
  props.listAttributes.map(attribute => ({
    label: attribute.attributeDisplayName,
    value: attribute.attributeKey,
  }))
);

const activeAttributeLabel = computed(() => {
  const match = props.listAttributes.find(
    attribute => attribute.attributeKey === props.groupByAttributeKey
  );
  return match?.attributeDisplayName || '';
});

const orderedColumns = computed({
  get: () =>
    props.columnOrder.map(value => ({
      value,
      label:
        value === UNASSIGNED_KANBAN_COLUMN_VALUE
          ? t('CONTACTS_LAYOUT.KANBAN.UNASSIGNED_COLUMN_LABEL')
          : value,
    })),
  set: items =>
    emit(
      'update:column-order',
      items.map(item => item.value)
    ),
});

const handleGroupByChange = attributeKey => {
  emit('update:group-by', attributeKey);
};

const toggleMenu = () => {
  isOpen.value = !isOpen.value;
};
</script>

<template>
  <div v-on-clickaway="() => (isOpen = false)" class="relative">
    <Button
      icon="i-lucide-settings-2"
      color="slate"
      size="sm"
      variant="ghost"
      :class="isOpen ? 'bg-n-alpha-2' : ''"
      @click="toggleMenu"
    />
    <div
      v-if="isOpen"
      class="absolute z-50 flex flex-col gap-4 p-4 mt-1 border rounded-xl top-full ltr:right-0 rtl:left-0 w-72 bg-n-alpha-3 backdrop-blur-[100px] border-n-weak"
    >
      <div class="flex items-center justify-between gap-2">
        <span class="text-sm text-n-slate-12">
          {{ t('CONTACTS_LAYOUT.KANBAN.SETTINGS.GROUP_BY_LABEL') }}
        </span>
        <SelectMenu
          :model-value="groupByAttributeKey"
          :options="attributeOptions"
          :label="activeAttributeLabel"
          @update:model-value="handleGroupByChange"
        />
      </div>
      <div class="flex flex-col gap-2">
        <span class="text-sm text-n-slate-12">
          {{ t('CONTACTS_LAYOUT.KANBAN.SETTINGS.COLUMNS_LABEL') }}
        </span>
        <Draggable
          v-model="orderedColumns"
          item-key="value"
          animation="200"
          ghost-class="opacity-50"
          handle=".kanban-column-drag-handle"
          class="flex flex-col gap-1"
        >
          <template #item="{ element }">
            <div
              class="flex items-center gap-2 px-2 py-1 rounded-md bg-n-alpha-1"
            >
              <Icon
                icon="i-lucide-grip-vertical"
                class="kanban-column-drag-handle cursor-move text-n-slate-10 size-4"
              />
              <span class="text-sm truncate text-n-slate-12">
                {{ element.label }}
              </span>
            </div>
          </template>
        </Draggable>
      </div>
    </div>
  </div>
</template>
